import { useEffect, useState } from "react";
import { Shield, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/common";
import { adminApi, setAdminToken, errMsg } from "@/lib/api";
import { CodeReveal } from "./CodeReveal";
import { toast } from "sonner";

function useBusy() {
  const [busy, setBusy] = useState(false);
  const run = (fn) => async (e) => { e.preventDefault(); setBusy(true); try { await fn(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); } };
  return [busy, run];
}

function LoginForm({ onLogged, onRecover }) {
  const [f, setF] = useState({ email: "", code: "" });
  const [busy, run] = useBusy();
  const submit = run(async () => { const { data } = await adminApi.post("/login", f); setAdminToken(data.token); onLogged(); });
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
      <h1 className="text-2xl font-bold">Admin Master</h1>
      <Field label="E-mail"><Input type="email" value={f.email} onChange={(e) => setF((x) => ({ ...x, email: e.target.value }))} required data-testid="admin-email-input" /></Field>
      <Field label="Código de acesso (10 dígitos)">
        <Input type="password" inputMode="numeric" autoComplete="current-password" maxLength={10} value={f.code} onChange={(e) => setF((x) => ({ ...x, code: e.target.value.replace(/\D/g, "") }))} className="font-mono tracking-widest" required data-testid="admin-code-input" />
      </Field>
      <Button type="submit" className="w-full h-11" disabled={busy || f.code.length !== 10} data-testid="admin-login-btn">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Entrar</Button>
      <button type="button" onClick={onRecover} className="block w-full text-center text-xs text-muted-foreground underline" data-testid="admin-recover-link">Perdi o código de acesso</button>
    </form>
  );
}

function SetupForm({ recover, onCode, onBack }) {
  const [f, setF] = useState({ email: "", password: "", email_code: "" });
  const [step, setStep] = useState(null); // null | { sent: bool }
  const [busy, run] = useBusy();
  const submit = run(async () => {
    const { data } = await adminApi.post("/setup", f);
    if (data.email_code_required) {
      setStep({ sent: data.email_sent });
      if (data.email_sent) toast.success("Enviamos um código de 6 dígitos para o e-mail do administrador.");
      return;
    }
    onCode(data.code);
  });
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="admin-setup-form">
      <h1 className="text-2xl font-bold">{recover ? "Recuperar acesso" : "Configuração inicial"}</h1>
      <p className="text-sm text-muted-foreground">
        {recover ? "Confirme o e-mail e a senha do administrador da plataforma para gerar um novo código. O código anterior deixa de funcionar."
          : "Confirme o e-mail e a senha do administrador da plataforma. O sistema vai gerar o seu código de acesso de 10 dígitos."}
      </p>
      <Field label="E-mail"><Input type="email" value={f.email} onChange={(e) => setF((x) => ({ ...x, email: e.target.value }))} required data-testid="admin-setup-email" /></Field>
      <Field label="Senha do administrador"><Input type="password" value={f.password} onChange={(e) => setF((x) => ({ ...x, password: e.target.value }))} required data-testid="admin-setup-password" /></Field>
      {step && (
        <>
          <p className="text-sm text-muted-foreground" data-testid="admin-setup-email-info">
            {step.sent ? "Digite o código de 6 dígitos que enviamos para o e-mail do administrador. Ele vale por 15 minutos."
              : "Não foi possível enviar o e-mail agora. Tente de novo em alguns minutos ou fale com o suporte técnico."}
          </p>
          <Field label="Código recebido por e-mail">
            <Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={f.email_code} onChange={(e) => setF((x) => ({ ...x, email_code: e.target.value.replace(/\D/g, "") }))} className="font-mono tracking-widest" data-testid="admin-setup-email-code" />
          </Field>
        </>
      )}
      <Button type="submit" className="w-full h-11" disabled={busy || (step?.sent && f.email_code.length !== 6)} data-testid="admin-setup-btn">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{step ? "Confirmar e gerar código" : "Gerar código de acesso"}</Button>
      {onBack && <button type="button" onClick={onBack} className="block w-full text-center text-xs text-muted-foreground underline" data-testid="admin-setup-back">Voltar para o login</button>}
    </form>
  );
}

export function MasterGate({ onLogged }) {
  const [mode, setMode] = useState(null);
  const [code, setCode] = useState("");
  useEffect(() => { adminApi.get("/setup-status").then((r) => setMode(r.data.configured ? "login" : "setup")).catch(() => setMode("login")); }, []);
  if (!mode) return null;
  return (
    <div className="min-h-screen grid place-items-center p-6 grain">
      <div className="card-surface w-full max-w-sm p-8 space-y-4">
        <Shield className="h-8 w-8 text-primary" />
        {code ? <CodeReveal code={code} onDone={() => { setCode(""); setMode("login"); }} />
          : mode === "login" ? <LoginForm onLogged={onLogged} onRecover={() => setMode("recover")} />
          : <SetupForm recover={mode === "recover"} onCode={setCode} onBack={mode === "recover" ? () => setMode("login") : null} />}
      </div>
    </div>
  );
}
